import {
  Box,
  Drawer,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { X } from "react-feather";
import { fdelete, fget, fpatch, fpost } from "../../../API/callsAPI";
import { MovieColabContext } from "../../../Context/MovieColabContext";
import { actionDispatchProjectFilters } from "../../../redux/reduxAssets";
import {
  selectAppliedFilters,
  selectAppliedFilterForTrackable,
  selectAppliedFilterIdsForTrackable,
} from "../../../redux/selectors/projectFilters";
import FilterLists from "./FilterLists";
import FilterSettings from "./FilterSettings";
import { isDirtyTuples, rowsToTuples, tuplesToRows } from "./filterRowUtils";

const OMIT_FILTER_FIELDS_SET = [
  "id",
  "trackable_ptr",
  "link",
  "file",
  "thumbnail",
  "project",
  "linked_hdr_maps",
  "assets",
  "file_path",
  "parent_sequence",
  "vp_linked_level_sequence",
  "shot",
];

const filterSelectableFields = (fields = []) =>
  fields.filter((field) => !OMIT_FILTER_FIELDS_SET.includes(field.name));

const PROJECT_FILTERS_STORAGE_KEY = "projectFilters.appliedFilters";

const CommonFilterComponent = ({ open, onClose, trackableType, projectId }) => {
  const dispatch = useDispatch();
  const { applyFilterToTrackable, clearAppliedFilterForTrackable } =
    actionDispatchProjectFilters(dispatch);
  const { trackableClass, loggedInUser } = useContext(MovieColabContext);
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [defaultFields, setDefaultFields] = useState([]);
  const [filterLists, setFilterLists] = useState([]);
  const [updateRefresher, setUpdateRefresher] = useState(0);
  const [currentSelectedFilter, setCurrentSelectedFilter] = useState(null);
  const [baselineTuples, setBaselineTuples] = useState([]);
  const [optionCache, setOptionCache] = useState({
    allowed_dept: null,
    status: null,
    type: null,
    task: null,
  });

  const trackableId = useMemo(
    () =>
      trackableClass.find(
        (trackable) =>
          trackable.model.toLowerCase() === trackableType?.toLowerCase(),
      )?.id,
    [trackableClass, trackableType],
  );

  const appliedFilterForTrackable = useSelector((state) =>
    selectAppliedFilterForTrackable(state, trackableId),
  );

  const appliedFilterIds = useSelector((state) =>
    selectAppliedFilterIdsForTrackable(state, trackableId),
  );
  const appliedFilters = useSelector(selectAppliedFilters);

  const resetDrawerSession = useCallback(() => {
    setTab(0);
    setRows([]);
    setCurrentSelectedFilter(null);
    setBaselineTuples([]);
    setFilterLists([]);
    setOptionCache({
      allowed_dept: null,
      status: null,
      type: null,
      task: null,
    });
  }, []);

  const handleDrawerClose = () => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        PROJECT_FILTERS_STORAGE_KEY,
        JSON.stringify(appliedFilters ?? []),
      );
    }
    resetDrawerSession();
    onClose();
  };

  const getDefaultFields = async () => {
    if (!trackableId) return;
    try {
      const res = await fget({
        url: `/trackables/trackable-field/?project=${projectId}&trackable=${trackableId}`,
      });
      const defaults = res.data.default_fields ?? [];
      const custom = res.data.custom_fields ?? [];
      setDefaultFields(filterSelectableFields([...defaults, ...custom]));
    } catch (error) {
      console.error("Error getting default fields", error);
    }
  };

  const getFilterLists = async () => {
    try {
      const res = await fget({
        url: `/trackables/filter/?project=${projectId}`,
      });
      if (res.status === 200) {
        setFilterLists(res.data.results ?? []);
      }
    } catch (error) {
      console.error("Error fetching filter lists", error);
    }
  };

  useEffect(() => {
    if (open && projectId) {
      getFilterLists();
    }
  }, [projectId, open, updateRefresher]);

  useEffect(() => {
    if (open && projectId && trackableType && trackableClass?.length > 0) {
      getDefaultFields();
    }
  }, [open, projectId, trackableType, trackableClass, trackableId]);

  const handleUpdateRefresher = () => {
    setUpdateRefresher((prev) => prev + 1);
  };

  const handleSaveSuccess = () => {
    setRows([]);
    setCurrentSelectedFilter(null);
    setBaselineTuples([]);
    handleUpdateRefresher();
  };

  const handleSelectSavedFilter = (filter) => {
    const tuples = filter.filters ?? [];
    const nextRows = tuplesToRows(tuples, `saved-${filter.id}`);
    setCurrentSelectedFilter(filter);
    setBaselineTuples(tuples);
    setRows(nextRows);
    setTab(0);
  };

  useEffect(() => {
    if (!open || !appliedFilterForTrackable?.id || rows.length > 0) return;
    handleSelectSavedFilter(appliedFilterForTrackable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appliedFilterForTrackable?.id, appliedFilterForTrackable?.filters]);

  const handleDeleteSavedFilter = async (filter) => {
    try {
      await fdelete({
        url: `/trackables/filter/${filter.id}/`,
      });

      if (
        trackableId != null &&
        Number(appliedFilterForTrackable?.id) === Number(filter.id)
      ) {
        clearAppliedFilterForTrackable({ trackableId });
      }

      if (Number(currentSelectedFilter?.id) === Number(filter.id)) {
        setCurrentSelectedFilter(null);
        setRows([]);
        setBaselineTuples([]);
      }

      handleUpdateRefresher();
    } catch (error) {
      console.error("Error deleting filter", error);
    }
  };

  const handleClear = () => {
    if (trackableId != null) {
      clearAppliedFilterForTrackable({ trackableId });
    }
    setRows([]);
    setCurrentSelectedFilter(null);
    setBaselineTuples([]);
  };

  const handleApply = async () => {
    if (!trackableId || rows.length === 0) return;

    const tuples = rowsToTuples(rows);
    if (tuples.length === 0) return;

    let filterToApply = null;

    try {
      if (currentSelectedFilter?.id) {
        if (isDirtyTuples(baselineTuples, tuples)) {
          const res = await fpatch({
            url: `/trackables/filter/${currentSelectedFilter.id}/`,
            data: {
              name: currentSelectedFilter.name,
              trackable: trackableId,
              project: projectId,
              filters: tuples,
            },
          });
          if (res.status !== 200 && res.status !== 201) return;
          filterToApply = res.data;
          setCurrentSelectedFilter(res.data);
          handleUpdateRefresher();
        } else {
          filterToApply = { ...currentSelectedFilter, filters: tuples };
        }
      } else {
        const res = await fpost({
          url: "/trackables/filter/",
          data: {
            trackable: trackableId,
            project: projectId,
            name: `${trackableType}_Filter`,
            filters: tuples,
          },
        });
        if (res.status !== 200 && res.status !== 201) return;
        filterToApply = res.data;
        setCurrentSelectedFilter(res.data);
        handleUpdateRefresher();
      }
    } catch (error) {
      console.error("Error saving filter before apply", error);
      return;
    }

    applyFilterToTrackable({ filter: filterToApply, trackableId });
    setBaselineTuples(tuples);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleDrawerClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.22)",
            top: `65 px`,
            [`@media (min-width: 1280px)`]: {
              top: `80px`,
            },
          },
        },
        paper: {
          sx: {
            width: { xs: "100%", sm: 600 },
            maxWidth: "100vw",
            bgcolor: "#1B1D28",
            color: "#F9F9F9",
            borderLeft: `1px solid #606479`,
            borderTop: `1px solid #606479`,
            boxSizing: "border-box",
            top: `65 px`,
            [`@media (min-width: 1280px)`]: {
              top: `80px`,
            },
            bottom: 0,
            height: "auto",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 2.5, pb: 1.5, flexShrink: 0 }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
          >
            <Typography variant="h5" sx={{ fontWeight: 500 }}>
              Filter
            </Typography>
            <IconButton
              onClick={handleDrawerClose}
              aria-label="Close filter"
              size="small"
              sx={{
                color: "#606479",
                "&:hover": {
                  color: "#F9F9F9",
                  bgcolor: "rgba(255,255,255,0.06)",
                },
              }}
            >
              <X size={32} />
            </IconButton>
          </Stack>
          <Typography
            variant="body2"
            sx={{ mt: 1.25, color: "#B1B5C9", lineHeight: 1.5, fontSize: 13 }}
          >
            Build a filter by choosing a field, operator, and value. Stack
            multiple conditions, then save the combination to reuse it anytime.
          </Typography>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          sx={{
            width: "100%",
            px: 2,
            minHeight: 40,
            borderBottom: `1px solid #606479`,
            "& .MuiTab-root": {
              color: "#606479",
              textTransform: "none",
              minHeight: 40,
              py: 1,
            },
            "& .Mui-selected": { color: `#F9F9F9 !important` },
            "& .MuiTabs-indicator": {
              bgcolor: "#6C65D9",
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <Tab label="Filter settings" />
          <Tab label="List of Filters" />
        </Tabs>

        <Box sx={{ flex: 1, overflow: "auto", p: 2.5, pt: 2 }}>
          {tab === 0 && (
            <FilterSettings
              rows={rows}
              setRows={setRows}
              defaultFields={defaultFields}
              trackableId={trackableId}
              projectId={projectId}
              handleUpdateRefresher={handleUpdateRefresher}
              currentSelectedFilter={currentSelectedFilter}
              baselineTuples={baselineTuples}
              appliedFilterForTrackable={appliedFilterForTrackable}
              onApply={handleApply}
              onClear={handleClear}
              onSaveSuccess={handleSaveSuccess}
              optionCache={optionCache}
              setOptionCache={setOptionCache}
              loggedInUser={loggedInUser}
              handleDeleteFilter={handleDeleteSavedFilter}
            />
          )}

          {tab === 1 && (
            <FilterLists
              filterLists={filterLists}
              trackableId={trackableId}
              appliedFilterIds={appliedFilterIds}
              selectedFilterId={currentSelectedFilter?.id}
              onSelectFilter={handleSelectSavedFilter}
              onDeleteFilter={handleDeleteSavedFilter}
              loggedInUser={loggedInUser}
            />
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default CommonFilterComponent;
